import { RowCrudStatus } from "../../common/types";
import {
  AiSearchResponse,
  AiSearchResponseCreateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { graphqlToDateTime } from "../../../common/utils";
import { aiSearchConversationDisplayId } from "../../aiSearchConversation/store/AiSearchConversationCommonStore";
import { safeJsonParse } from "../../../common/utils";
import { EvaluationRating } from "../../../api/graphql";
import { userDisplayId } from "../../user/store/UserCommonStore";
import {
  AiSearchToolCallValuesType,
  getAiSearchToolCallValues,
  getAiSearchToolCallCreateInput,
} from "../../aiSearchToolCall/store/AiSearchToolCallCommonStore";
import { addedStatusList } from "../../common/types";

export type AiSearchResponseValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  __conversation: string;
  showConversation: { displayId: string; id: string } | null;
  query: string;
  response: string;
  sources: string;
  explanation: string;
  tokenUsage: string;
  createdAt: Date | null;
  rating: EvaluationRating | "";
  evaluatedAt: Date | null;
  __evaluatedBy: string;
  showEvaluatedBy: { displayId: string; id: string } | null;
  showToolCalls: AiSearchToolCallValuesType[];
};

export type AiSearchResponseErrorsType = {
  conversation?: { message: string; fieldLabel: string };
  query?: { message: string; fieldLabel: string };
  response?: { message: string; fieldLabel: string };
  sources?: { message: string; fieldLabel: string };
  explanation?: { message: string; fieldLabel: string };
  tokenUsage?: { message: string; fieldLabel: string };
  createdAt?: { message: string; fieldLabel: string };
  rating?: { message: string; fieldLabel: string };
  evaluatedAt?: { message: string; fieldLabel: string };
  evaluatedBy?: { message: string; fieldLabel: string };
  toolCalls?: { message: string; fieldLabel: string };
};

export function AiSearchResponseValuesInit(): AiSearchResponseValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    __conversation: "",
    showConversation: null,
    query: "",
    response: "",
    sources: "{}",
    explanation: "{}",
    tokenUsage: "{}",
    createdAt: null,
    rating: "" as EvaluationRating | "",
    evaluatedAt: null,
    __evaluatedBy: "",
    showEvaluatedBy: null,
    showToolCalls: [],
  };
}

export type AiSearchResponseType =
  | AiSearchResponse
  | AiSearchResponseValuesType;

export function aiSearchResponseDisplayId(
  source: Partial<AiSearchResponse>,
  _langStore: LangStore
) {
  return `${graphqlToDateTime(source.createdAt)}`;
}

export function getAiSearchResponseValues(
  source: AiSearchResponseType,
  _langStore: LangStore,
  _duplicated: boolean = false
): AiSearchResponseValuesType {
  const entityValues = AiSearchResponseValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : aiSearchResponseDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.showConversation =
    "conversation" in source && source.conversation
      ? {
          id: source.conversation.id,
          displayId: aiSearchConversationDisplayId(
            source.conversation,
            _langStore
          ),
        }
      : "showConversation" in source && source.showConversation
      ? {
          id: source.showConversation.id,
          displayId: source.showConversation.displayId,
        }
      : null;
  entityValues.__conversation = entityValues.showConversation?.displayId || "";
  entityValues.query = source.query;
  entityValues.response = source.response;
  entityValues.sources = source.sources || "";
  entityValues.explanation = source.explanation || "";
  entityValues.tokenUsage = source.tokenUsage || "";
  entityValues.createdAt = new Date(source.createdAt);
  entityValues.rating = source.rating || ("" as EvaluationRating | "");
  entityValues.evaluatedAt = source.evaluatedAt
    ? new Date(source.evaluatedAt)
    : null;
  entityValues.showEvaluatedBy =
    "evaluatedBy" in source && source.evaluatedBy
      ? {
          id: source.evaluatedBy.id,
          displayId: userDisplayId(source.evaluatedBy, _langStore),
        }
      : "showEvaluatedBy" in source && source.showEvaluatedBy
      ? {
          id: source.showEvaluatedBy.id,
          displayId: source.showEvaluatedBy.displayId,
        }
      : null;
  entityValues.__evaluatedBy = entityValues.showEvaluatedBy?.displayId || "";
  entityValues.showToolCalls =
    "toolCalls" in source && source.toolCalls
      ? (source.toolCalls || []).map((x) => {
          const row: AiSearchToolCallValuesType = getAiSearchToolCallValues(
            x,
            _langStore,
            _duplicated
          );
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showToolCalls" in source && source.showToolCalls
      ? (source.showToolCalls || []).map((x) => {
          const row: AiSearchToolCallValuesType = getAiSearchToolCallValues(
            x,
            _langStore,
            _duplicated
          );
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getAiSearchResponseCreateVariables(
  values: AiSearchResponseValuesType
): AiSearchResponseCreateMutationVariables {
  return {
    conversationId: values.showConversation?.id || "",
    query: values.query,
    response: values.response,
    sources: safeJsonParse(values.sources, "sources"),
    explanation: safeJsonParse(values.explanation, "explanation"),
    tokenUsage: safeJsonParse(values.tokenUsage, "tokenUsage"),
    rating: values.rating || null,
    evaluatedAt: values.evaluatedAt?.toISOString() || null,
    evaluatedById: values.showEvaluatedBy?.id || "",
    toolCalls: values.showToolCalls
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getAiSearchToolCallCreateInput(x)),
  };
}
